/**
 * Utility functions for leaderboard styling and ranking
 */

/**
 * Color scheme configuration for different ranks
 */
const RANK_COLORS = {
  1: { bg: "bg-slate-700", text: "text-white", progress: "bg-slate-700" },
  2: { bg: "bg-slate-600", text: "text-white", progress: "bg-slate-600" },
  3: { bg: "bg-slate-500", text: "text-white", progress: "bg-slate-500" },
  4: { bg: "bg-slate-400", text: "text-slate-900", progress: "bg-slate-400" },
  5: { bg: "bg-slate-300", text: "text-slate-900", progress: "bg-slate-300" },
} as const;

/**
 * Default colors for ranks beyond the defined range
 */
const DEFAULT_COLORS = {
  bg: "bg-slate-200",
  text: "text-slate-700",
  progress: "bg-slate-200"
} as const;

/**
 * Get the appropriate color classes for a given rank
 * @param rank - The rank position (1-based)
 * @returns Object containing background, text, and progress bar color classes
 */
export function getRankColors(rank: number): {
  background: string;
  text: string;
  progress: string;
} {
  const colors = RANK_COLORS[rank as keyof typeof RANK_COLORS] || DEFAULT_COLORS;
  
  return {
    background: colors.bg,
    text: colors.text,
    progress: colors.progress
  };
}

/**
 * Get the background and text color classes for a rank badge
 * @param rank - The rank position (1-based)
 * @returns Combined background and text color classes
 */
export function getRankBadgeClasses(rank: number): string {
  const colors = getRankColors(rank);
  return `${colors.background} ${colors.text}`;
}

/**
 * Get the progress bar color class for a given rank
 * @param rank - The rank position (1-based)
 * @returns Progress bar color class
 */
export function getProgressBarColor(rank: number): string {
  const colors = getRankColors(rank);
  return colors.progress;
}

/**
 * Get special styling for the first place (winner) rank
 * @param rank - The rank position (1-based)
 * @returns Additional CSS classes for first place styling
 */
export function getWinnerStyling(rank: number): string {
  return rank === 1 ? "ring-2 ring-yellow-400 ring-opacity-50" : "";
}

/**
 * Get hover effects based on rank
 * @param rank - The rank position (1-based)
 * @returns Hover effect classes
 */
export function getRankHoverEffects(rank: number): string {
  const baseHover = "hover:shadow-md transition-all duration-200";
  
  if (rank === 1) {
    return `${baseHover} hover:shadow-yellow-100`;
  }
  
  return baseHover;
}
